import { BigTimeRawMatterArray, Matter, MatterType } from '../types';
import { parseDate } from './dateUtils';

// BigTime API returns arrays with values at specific indices
// Based on the metadata column positions (Position = array index)
const BIGTIME_INDICES = {
  CLIENT: 1,                   // [0] clinm: Client (string)
  NAME: 3,                     // [1] prjnm: Name (string)
  DISPLAY_NAME: 5,             // [2] dname: Display Name (string)
  CASE_LOCATION: 7,            // [3] prjccenter2: Case Location (string)
  PROJECT_ID: 4,               // [4] prjid: Project ID (string) - for BigTime deep links
  TYPE: 10,                     // [5] prjtype: Type (string)
  LEAD_CONSULTANT: 13,          // [5] prjteam900472: Lead Consultant (string)
  SALES_LEAD_CONSULTANT: 15,    // [6] prjteam900482: Sales/Lead Consultant (string)
  TECH_AND_LEAD: 17,            // [7] prjteam900486: Tech and Lead (string)
  SECONDARY_CONSULTANT: 19,     // [8] prjteam900473: Secondary Consultant (string)
  TECH_AND_SECOND: 21,          // [9] prjteam900483: Tech and Second (string)
  TECHNICIAN: 23,              // [10] prjteam900474: Technician (string)
  JE_VIDEO: 25,                // [11] prjteam900447: JE Video (string)
  GRAPHIC_ARTIST: 27,          // [12] prjteam900452: Graphic Artist (string)
  GRAPHIC_CONSULTING: 29,      // [13] prjteam900466: Graphic Consulting (string)
  DUE_DATE: 31,                // [14] prjenddt: Due Date (date)
  TRIAL_PRESENTATION: 32,      // [15] prjteam900453: Trial Presentation (string)
  FOCUS_GROUP_ADDRESS: 34,     // udf_jc__fg_address: Focus Group Address (string)
  REPORT_TURNAROUND: 35,        // udf_jc__report_turnaround: Report Turnaround (string)
  ADDITIONAL_DATE_1: 36,       // [16] udf_jc__additional_date_1: JC - Additional Date 1 (date)
  ADDITIONAL_DATE_2: 37,       // [17] udf_jc__additional_date_2: JC - Additional Date 2 (date)
  ADDITIONAL_DATE_3: 38,       // [18] udf_jc__additional_date_3: JC - Additional Date 3 (date)
  ADDITIONAL_DATE_4: 39,       // [19] udf_jc__additional_date_4: JC - Additional Date 4 (date)
  ADDITIONAL_DATE_5: 40,       // [20] udf_jc__additional_date_5: JC - Additional Date 5 (date)
  SPAN_DATE: 41,               // [21] udf_jc__span_date: JC - Span Date (date)
  DELIBERATION_GROUPS: 42,     // [22] udf_jc__deliberation_groups_: Deliberation Groups (#) (string)
  NOTES: 43,                   // [23] udf_jc__matter_calendar_notes: JC - Matter Calendar Notes (string)
  ADDITIONAL_PROJECT_TYPE_1: 44, // [24] udf_additionalprojecttype1: Additional Project Type 1 (string)
  ADDITIONAL_PROJECT_TYPE_2: 45, // [25] udf_additionalprojecttype2: Additional Project Type 2 (string)
  ADDITIONAL_PROJECT_TYPE_3: 46, // [26] udf_additionalprojecttype3: Additional Project Type 3 (string)
  ADDITIONAL_PROJECT_TYPE_4: 47, // [27] udf_additionalprojecttype4: Additional Project Type 4 (string)
  ADDITIONAL_PROJECT_TYPE_5: 48, // [28] udf_additionalprojecttype5: Additional Project Type 5 (string)
} as const;

/**
 * Transform BigTime raw array data to application Matter format
 */
export const transformBigTimeData = (rawData: BigTimeRawMatterArray[]): Matter[] => {
  return rawData.map((raw, index) => {
    const displayName = String(raw[BIGTIME_INDICES.DISPLAY_NAME] || '');
    const bigtimeProjectId = String(raw[BIGTIME_INDICES.PROJECT_ID] || '');
    const projectId = bigtimeProjectId || extractMatterId(displayName) || `matter-${index}`;

    // Collect ALL dates found in the array (since they're not in predictable positions)
    const allDates: string[] = [];
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;

    raw.forEach((item, pos) => {
      if (typeof item === 'string' && datePattern.test(item.trim())) {
        allDates.push(item.trim());
      }
    });

    // Try to get due date from expected position, otherwise use first found date
    const expectedDueDate = String(raw[BIGTIME_INDICES.DUE_DATE] || '').trim();
    let actualDueDate = (expectedDueDate && expectedDueDate !== '' && expectedDueDate !== 'undefined') ? expectedDueDate : undefined;
    if (!actualDueDate && allDates.length > 0) {
      actualDueDate = allDates[0];
    }

    const spanDate = String(raw[BIGTIME_INDICES.SPAN_DATE] || '').trim();

    // Additional dates are all dates except the ones we used as due date and span date
    const additionalDates = allDates.filter(date => date !== actualDueDate && date !== spanDate);

    // Collect additional project types
    const additionalProjectTypes: string[] = [];
    [
      BIGTIME_INDICES.ADDITIONAL_PROJECT_TYPE_1,
      BIGTIME_INDICES.ADDITIONAL_PROJECT_TYPE_2,
      BIGTIME_INDICES.ADDITIONAL_PROJECT_TYPE_3,
      BIGTIME_INDICES.ADDITIONAL_PROJECT_TYPE_4,
      BIGTIME_INDICES.ADDITIONAL_PROJECT_TYPE_5,
    ].forEach(typeIndex => {
      const type = String(raw[typeIndex] || '').trim();
      if (type && type !== '' && type !== 'null') {
        additionalProjectTypes.push(type);
      }
    });

    const leadConsultant = formatConsultantNames(String(raw[BIGTIME_INDICES.LEAD_CONSULTANT] || ''));
    const secondaryConsultant = formatConsultantNames(String(raw[BIGTIME_INDICES.SECONDARY_CONSULTANT] || ''));
    const technician = formatConsultantNames(String(raw[BIGTIME_INDICES.TECHNICIAN] || ''));
    const trialPresentation = formatConsultantNames(String(raw[BIGTIME_INDICES.TRIAL_PRESENTATION] || ''));

    // Debug: Search for ANY dates in the array (since they're not in expected positions)
    if (index < 5) {
      const datePattern = /^\d{4}-\d{2}-\d{2}$/;
      const datesFound: {pos: number, value: string}[] = [];
      raw.forEach((item, pos) => {
        if (typeof item === 'string' && datePattern.test(item)) {
          datesFound.push({pos, value: item});
        }
      });
      if (datesFound.length > 0) {
        console.log(`Matter ${index} "${raw[BIGTIME_INDICES.DISPLAY_NAME]}" has dates:`, datesFound);
      }
    }
    const deliberationGroups = String(raw[BIGTIME_INDICES.DELIBERATION_GROUPS] || '').trim();
    const notes = String(raw[BIGTIME_INDICES.NOTES] || '').trim();

    // Normalize the matter type and preserve original if needed
    const typeInfo = normalizeMatterType(String(raw[BIGTIME_INDICES.TYPE] || ''));

    return {
      id: projectId,
      client: String(raw[BIGTIME_INDICES.CLIENT] || ''),
      name: String(raw[BIGTIME_INDICES.NAME] || ''),
      displayName,
      caseLocation: String(raw[BIGTIME_INDICES.CASE_LOCATION] || ''),
      type: typeInfo.type,
      originalType: typeInfo.originalType,
      leadConsultant: leadConsultant || 'Unassigned',
      secondaryConsultant: secondaryConsultant || undefined,
      technician: technician || undefined,
      dueDate: actualDueDate,
      trialPresentation: (trialPresentation && trialPresentation !== '' && trialPresentation !== 'undefined') ? trialPresentation : undefined,
      additionalDates,
      spanDate: (spanDate && spanDate !== '' && spanDate !== 'undefined') ? spanDate : undefined,
      deliberationGroups: deliberationGroups || undefined,
      notes: notes || undefined,
      additionalProjectTypes,
      // BigTime specific fields
      projectId,
      bigtimeProjectId,
      salesLeadConsultant: parseNumber(raw[BIGTIME_INDICES.SALES_LEAD_CONSULTANT]) ?? undefined,
      techAndLead: parseNumber(raw[BIGTIME_INDICES.TECH_AND_LEAD]) ?? undefined,
      techAndSecond: parseNumber(raw[BIGTIME_INDICES.TECH_AND_SECOND]) ?? undefined,
      jeVideo: parseNumber(raw[BIGTIME_INDICES.JE_VIDEO]) ?? undefined,
      graphicArtist: parseNumber(raw[BIGTIME_INDICES.GRAPHIC_ARTIST]) ?? undefined,
      graphicConsulting: String(raw[BIGTIME_INDICES.GRAPHIC_CONSULTING] || '').trim() || undefined,
      focusGroupAddress: String(raw[BIGTIME_INDICES.FOCUS_GROUP_ADDRESS] || '').trim() || undefined,
      reportTurnaround: String(raw[BIGTIME_INDICES.REPORT_TURNAROUND] || '').trim() || undefined,
    };
  });
};

/**
 * Extract matter ID from display name (e.g., "ARB-7068 30b6 Witness Academy" -> "ARB-7068")
 */
const extractMatterId = (displayName: string): string | null => {
  if (!displayName) return null;

  // Common patterns for matter IDs
  const patterns = [
    /^([A-Z]{2,4}-\d{4,6})/, // ARB-7068, CNA-6393, etc.
    /^([A-Z]{3}-\d{4})/, // LOB-6824, etc.
    /^([A-Z]+\d+)/, // ABC123, etc.
  ];

  for (const pattern of patterns) {
    const match = displayName.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
};

/**
 * Generate a unique ID if none can be extracted
 */
const generateId = (raw: BigTimeRawMatterArray): string => {
  const client = String(raw[BIGTIME_INDICES.CLIENT] || 'UNK');
  const clientCode = client.substring(0, 3).toUpperCase();
  const name = String(raw[BIGTIME_INDICES.NAME] || raw[BIGTIME_INDICES.DISPLAY_NAME] || '');
  const nameHash = hashString(name);
  return `${clientCode}-${nameHash}`;
};

/**
 * Simple string hash function for generating IDs
 */
const hashString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString().substring(0, 4);
};

/**
 * Normalize matter type to known types, fallback to 'Other' with original preserved
 * Returns { type: MatterType, originalType?: string }
 */
const normalizeMatterType = (type: string): { type: MatterType; originalType?: string } => {
  if (!type || type.trim() === '') {
    return { type: 'Other', originalType: type };
  }

  const normalizedType = type.toLowerCase().trim();

  // Enhanced type mapping with more variations
  const typeMap: Record<string, MatterType> = {
    // Witness Prep variations
    'witness prep': 'Witness Prep',
    'witness preparation': 'Witness Prep',
    'witness coaching': 'Witness Prep',
    'prep witness': 'Witness Prep',

    // Focus Group variations
    'focus group': 'Focus Group',
    'focus groups': 'Focus Group',
    'fg': 'Focus Group',

    // Jury Selection variations
    'jury selection': 'Jury Selection',
    'voir dire': 'Jury Selection',
    'jury consulting': 'Jury Selection',
    'js': 'Jury Selection',

    // Jury Confirm variations
    'jury confirm': 'Jury Confirm',
    'jury confirmation': 'Jury Confirm',
    'confirm jury': 'Jury Confirm',

    // Shadow Jury variations
    'shadow jury': 'Shadow Jury',
    'shadow juror': 'Shadow Jury',
    'sj': 'Shadow Jury',

    // Jury Scout variations
    'jury scout': 'Jury Scout',
    'jury scouting': 'Jury Scout',
    'scout': 'Jury Scout',

    // Cogent Prep variations
    'cogent prep': 'Cogent Prep',
    'cogent preparation': 'Cogent Prep',
    'cogent': 'Cogent Prep',

    // Research variations
    'research pending': 'Research Pending',
    'research': 'Research Pending',
    'legal research': 'Research Pending',
    'case research': 'Research Pending',
  };

  const originalTypeTrimmed = type.trim();

  // First try exact match
  const exactMatch = typeMap[normalizedType];
  if (exactMatch) {
    // Preserve original if it's different from the exact match
    const shouldPreserveOriginal = originalTypeTrimmed.toLowerCase() !== exactMatch.toLowerCase();
    return {
      type: exactMatch,
      originalType: shouldPreserveOriginal ? originalTypeTrimmed : undefined
    };
  }

  // Then try "starts with" matching for things like "Focus Group - 2 Day"
  const startsWithMatches = [
    { prefix: 'witness prep', type: 'Witness Prep' as MatterType },
    { prefix: 'witness preparation', type: 'Witness Prep' as MatterType },
    { prefix: 'focus group', type: 'Focus Group' as MatterType },
    { prefix: 'jury selection', type: 'Jury Selection' as MatterType },
    { prefix: 'jury confirm', type: 'Jury Confirm' as MatterType },
    { prefix: 'shadow jury', type: 'Shadow Jury' as MatterType },
    { prefix: 'jury scout', type: 'Jury Scout' as MatterType },
    { prefix: 'cogent prep', type: 'Cogent Prep' as MatterType },
    { prefix: 'research', type: 'Research Pending' as MatterType },
  ];

  for (const { prefix, type: matterType } of startsWithMatches) {
    if (normalizedType.startsWith(prefix)) {
      // Always preserve original when using "starts with" matching since it's definitely different
      return { type: matterType, originalType: originalTypeTrimmed };
    }
  }

  // No match found, return as Other with original preserved
  return { type: 'Other', originalType: originalTypeTrimmed };
};

/**
 * Transform CSV data to BigTime raw format (deprecated - kept for reference)
 * Note: BigTime API now returns array-based data, not object-based
 */

/**
 * Helper function to parse numbers safely
 */
const parseNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return isNaN(num) ? null : num;
};

/**
 * Helper function to format consultant names with proper spacing
 * Converts "John Adams,Stacy True" to "John Adams, Stacy True"
 */
const formatConsultantNames = (consultantString: string): string => {
  if (!consultantString || consultantString.trim() === '') {
    return '';
  }

  return consultantString
    .split(',')
    .map(name => name.trim())
    .filter(name => name.length > 0)
    .join(', ');
};

/**
 * Extract unique consultants from matters
 */
export const extractConsultants = (matters: Matter[]): string[] => {
  const consultantSet = new Set<string>();

  matters.forEach(matter => {
    if (matter.leadConsultant) {
      // Handle comma-separated consultants
      matter.leadConsultant.split(',').forEach(consultant => {
        consultantSet.add(consultant.trim());
      });
    }

    if (matter.secondaryConsultant) {
      matter.secondaryConsultant.split(',').forEach(consultant => {
        consultantSet.add(consultant.trim());
      });
    }

    if (matter.technician) {
      matter.technician.split(',').forEach(consultant => {
        consultantSet.add(consultant.trim());
      });
    }

    if (matter.trialPresentation) {
      matter.trialPresentation.split(',').forEach(consultant => {
        consultantSet.add(consultant.trim());
      });
    }
  });

  return Array.from(consultantSet)
    .filter(name => name.length > 0)
    .sort();
};

/**
 * Extract unique clients from matters
 */
export const extractClients = (matters: Matter[]): string[] => {
  const clientSet = new Set<string>();

  matters.forEach(matter => {
    if (matter.client) {
      clientSet.add(matter.client.trim());
    }
  });

  return Array.from(clientSet)
    .filter(name => name.length > 0)
    .sort();
};

/**
 * Extract unique locations from matters
 */
export const extractLocations = (matters: Matter[]): string[] => {
  const locationSet = new Set<string>();

  matters.forEach(matter => {
    if (matter.caseLocation) {
      locationSet.add(matter.caseLocation.trim());
    }
  });

  return Array.from(locationSet)
    .filter(name => name.length > 0)
    .sort();
};

/**
 * Filter matters by various criteria
 */
export const filterMatters = (
  matters: Matter[],
  filters: {
    matterType?: string | string[];
    consultant?: string;
    client?: string;
    location?: string;
    searchTerm?: string;
  }
): Matter[] => {
  return matters.filter(matter => {
    // Matter type filter
    if (filters.matterType) {
      if (Array.isArray(filters.matterType)) {
        if (filters.matterType.length > 0 && !filters.matterType.includes(matter.type)) {
          return false;
        }
      } else if (filters.matterType !== 'All' && matter.type !== filters.matterType) {
        return false;
      }
    }

    // Consultant filter
    if (filters.consultant && filters.consultant !== 'All') {
      const consultantMatch = [
        matter.leadConsultant,
        matter.secondaryConsultant,
        matter.technician,
        matter.trialPresentation,
      ].some(
        consultant =>
          consultant && consultant.toLowerCase().includes(filters.consultant!.toLowerCase())
      );
      if (!consultantMatch) return false;
    }

    // Client filter
    if (filters.client && filters.client !== 'All' && matter.client !== filters.client) {
      return false;
    }

    // Location filter
    if (
      filters.location &&
      filters.location !== 'All' &&
      matter.caseLocation !== filters.location
    ) {
      return false;
    }

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableText = [
        matter.displayName,
        matter.client,
        matter.name,
        matter.caseLocation,
        matter.notes,
      ]
        .join(' ')
        .toLowerCase();

      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });
};
